import type { AuthTokens } from "@/types/api";
import {
  SESSION_EXPIRED_EVENT,
  buildAuthRedirectPath,
} from "@/api/sessionExpired.mjs";
import { acceptLanguageHeader } from "@/api/acceptLanguage.mjs";
import { useStore } from "@/store/useStore";

const ACCESS_KEY = "rubby-front-access-token";
const REFRESH_KEY = "rubby-front-refresh-token";
const USER_KEY = "rubby-front-user";

export interface StoredUser {
  userId: string;
  email: string;
  roles: string[];
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function getStoredUser(): StoredUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredUser;
  } catch {
    return null;
  }
}

export function persistAuth(tokens: AuthTokens): void {
  localStorage.setItem(ACCESS_KEY, tokens.accessToken);
  localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
  localStorage.setItem(
    USER_KEY,
    JSON.stringify({
      userId: tokens.userId,
      email: tokens.email,
      roles: tokens.roles,
    }),
  );
}

export function clearAuth(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

function normalizeBaseUrl(url: string | undefined | null): string {
  return String(url ?? "").replace(/\/$/, "");
}

const BASE_URL = normalizeBaseUrl(
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3001",
);

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

function parseErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object") {
    const body = err as { message?: unknown; code?: string };
    if (Array.isArray(body.message)) return body.message.join(". ");
    if (typeof body.message === "string") return body.message;
  }
  return fallback;
}

type RequestOptions = RequestInit & { skipAuth?: boolean; _retried?: boolean };

let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;
    try {
      const res = await fetch(`${BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        clearAuth();
        return false;
      }
      const data = (await res.json()) as AuthTokens;
      persistAuth(data);
      return true;
    } catch {
      clearAuth();
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

let sessionExpiredPromise: Promise<never> | null = null;

/**
 * Clear tokens, drop React auth state, and send the user to /auth.
 * Returns a promise that never settles so callers cannot toast this as a failure.
 */
function expireSession(): Promise<never> {
  if (sessionExpiredPromise) return sessionExpiredPromise;

  sessionExpiredPromise = new Promise<never>(() => {
    clearAuth();
    if (typeof window === "undefined") return;
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    const path = buildAuthRedirectPath(
      window.location.pathname,
      window.location.search,
    );
    if (path) window.location.assign(path);
  });

  return sessionExpiredPromise;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> | undefined),
  };

  if (!(options.body instanceof FormData) && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  if (!headers["Accept-Language"]) {
    headers["Accept-Language"] = acceptLanguageHeader(useStore.getState().language);
  }

  if (!options.skipAuth) {
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (res.status === 401 && !options.skipAuth) {
    if (!options._retried) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        return api<T>(path, { ...options, _retried: true });
      }
    }
    return expireSession();
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(
      parseErrorMessage(errBody, `خطای HTTP ${res.status}`),
      res.status,
      typeof (errBody as { code?: string }).code === "string"
        ? (errBody as { code: string }).code
        : undefined,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
